import React, { useState, useEffect, useMemo } from 'react';
import { leaveApi, employeeApi, compoffApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, CalendarOff, FileText, Send } from 'lucide-react';

const normalizeCompOffAsLeave = (compoff) => {
  if (!compoff) return null;
  const id = compoff.compoffId || compoff.leaveId || compoff.id || compoff._id;
  return {
    ...compoff,
    leaveId: id,
    compoffId: id,
    type: 'CompOff',
    isCompOff: true,
  };
};

const parseCompOffBalance = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return Number(value) || 0;
  return (
    Number(value?.CompOff ?? value?.CO ?? value?.balance ?? value?.available ?? value?.count ?? 0) || 0
  );
};

const normalizeArrayResponse = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.leaves)) return value.leaves;
  return [];
};

export default function EmployeePortalPage() {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [myBalance, setMyBalance] = useState(null);
  const [compOffBalance, setCompOffBalance] = useState(0);
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);

  const [applyOpen, setApplyOpen] = useState(false);
  const [form, setForm] = useState({ type: 'EL', startDate: '', endDate: '', reason: '' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leavesData, compoffData] = await Promise.all([
          leaveApi.getAll().catch(() => []),
          compoffApi.getAll().catch(() => []),
        ]);
        const leaveItems = normalizeArrayResponse(leavesData);
        const compoffItems = normalizeArrayResponse(compoffData)
          .map(normalizeCompOffAsLeave)
          .filter(Boolean);
        const merged = [...leaveItems, ...compoffItems].sort((a, b) => {
          const aTime = new Date(a?.appliedOn || a?.createdAt || 0).getTime();
          const bTime = new Date(b?.appliedOn || b?.createdAt || 0).getTime();
          return bTime - aTime;
        });
        setLeaves(merged);

        if (user?.employeeId) {
          const [balance, empData, cofBalance] = await Promise.all([
            leaveApi.getBalance(user.employeeId).catch(() => null),
            employeeApi.getById(user.employeeId).catch(() => null),
            compoffApi.getBalance(user.employeeId).catch(() => null),
          ]);
          setMyBalance(balance);
          setEmp(empData);
          setCompOffBalance(parseCompOffBalance(cofBalance));
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const myLeaves = useMemo(() => {
    return leaves.filter(l => l.employeeId === user?.employeeId)
      .sort((a, b) => new Date(b.appliedOn) - new Date(a.appliedOn));
  }, [leaves, user]);

  const pendingCount = myLeaves.filter(l => l.status === 'Pending').length;
  const approvedCount = myLeaves.filter(l => l.status === 'Approved').length;
  const rejectedCount = myLeaves.filter(l => l.status === 'Rejected').length;

  const handleApply = async () => {
    if (!form.startDate || !form.endDate || !form.reason) {
      toast.error('Please fill all fields');
      return;
    }
    if (new Date(form.endDate) < new Date(form.startDate)) {
      toast.error('End date must be after start date');
      return;
    }

    const startD = new Date(form.startDate);
    const endD = new Date(form.endDate);
    const dayCount = Math.ceil((endD - startD) / 86400000) + 1;

    if (form.type === 'CompOff') {
      if ((compOffBalance || 0) < dayCount) {
        toast.error(`Insufficient CompOff balance. Available: ${compOffBalance || 0} days, Requested: ${dayCount} days`);
        return;
      }
    } else if (form.type !== 'LOP' && form.type !== 'ONDUTY' && myBalance) {
      if ((myBalance[form.type] || 0) < dayCount) {
        toast.error(`Insufficient ${form.type} balance. Available: ${myBalance[form.type] || 0} days, Requested: ${dayCount} days`);
        return;
      }
    }

    try {
      let newLeave;
      if (form.type === 'CompOff') {
        const created = await compoffApi.apply({
          startDate: form.startDate,
          endDate: form.endDate,
          reason: form.reason,
        });
        newLeave = normalizeCompOffAsLeave(created);
      } else {
        newLeave = await leaveApi.apply({
          type: form.type,
          startDate: form.startDate,
          endDate: form.endDate,
          reason: form.reason,
        });
      }
      setLeaves(prev => [newLeave, ...prev]);
      toast.success(form.type === 'CompOff' ? 'CompOff application submitted' : `Leave application submitted for ${dayCount} day(s)`);
      setApplyOpen(false);
      setForm({ type: 'EL', startDate: '', endDate: '', reason: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to apply for leave');
    }
  };

  const statusColor = (status) => {
    if (status === 'Approved') return 'bg-green-100 text-green-700 border-green-200';
    if (status === 'Pending') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="employee-portal-page" className="space-y-3 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-[Manrope]">My Portal</h1>
          <p className="text-muted-foreground mt-1">Welcome, {emp ? `${emp.firstName} ${emp.lastName}` : user?.name}</p>
        </div>
        <Button onClick={() => setApplyOpen(true)} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="portal-apply-leave-btn">
          <Plus className="w-4 h-4 mr-2" /> Apply for Leave
        </Button>
      </div>

      {/* Leave Balance Cards */}
      {(myBalance || compOffBalance != null) && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Earned Leave</p>
              <p className="text-2xl font-bold font-mono mt-1 text-[#0047AB]">{myBalance.EL || 0}</p>
              <p className="text-[10px] text-muted-foreground">Available</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Casual Leave</p>
              <p className="text-2xl font-bold font-mono mt-1 text-[#6366f1]">{myBalance.CL || 0}</p>
              <p className="text-[10px] text-muted-foreground">Available</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Medical Leave</p>
              <p className="text-2xl font-bold font-mono mt-1 text-[#10b981]">{myBalance.ML || 0}</p>
              <p className="text-[10px] text-muted-foreground">Available</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">LOP Taken</p>
              <p className="text-2xl font-bold font-mono mt-1 text-[#ef4444]">{myBalance.LOP || 0}</p>
              <p className="text-[10px] text-muted-foreground">Days</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">CompOff</p>
              <p className="text-2xl font-bold font-mono mt-1 text-[#f59e0b]">{compOffBalance || 0}</p>
              <p className="text-[10px] text-muted-foreground">Available</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Pending</p>
              <p className="text-2xl font-bold font-mono mt-1 text-[#f59e0b]">{pendingCount}</p>
              <p className="text-[10px] text-muted-foreground">Requests</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Approved</p>
              <p className="text-2xl font-bold font-mono mt-1 text-[#10b981]">{approvedCount}</p>
              <p className="text-[10px] text-muted-foreground">This Year</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Rejected</p>
              <p className="text-2xl font-bold font-mono mt-1 text-[#ef4444]">{rejectedCount}</p>
              <p className="text-[10px] text-muted-foreground">This Year</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Leave History */}
      <Card className="border border-border shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-[Manrope] flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#0047AB]" />
            My Leave Applications
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs uppercase tracking-widest">ID</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">From</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">To</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Days</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Reason</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Applied On</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Reviewer Comment</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {myLeaves.map(leave => {
                const startD = new Date(leave.startDate);
                const endD = new Date(leave.endDate);
                const dayCount = leave.dayCount || Math.ceil((endD - startD) / 86400000) + 1;
                return (
                  <TableRow key={leave.leaveId || leave.id || leave.compoffId} data-testid={`portal-leave-${leave.leaveId || leave.id || leave.compoffId}`}>
                    <TableCell className="font-mono text-xs text-muted-foreground">{leave.leaveId || leave.id || leave.compoffId}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{leave.type}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{leave.startDate}</TableCell>
                    <TableCell className="font-mono text-xs">{leave.endDate}</TableCell>
                    <TableCell className="font-mono text-xs text-center font-bold">{dayCount}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{leave.reason}</TableCell>
                    <TableCell>
                      <Badge className={statusColor(leave.status)}>{leave.status}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{new Date(leave.appliedOn).toLocaleDateString()}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">
                      {leave.reviewComment || '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
              {myLeaves.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                    <CalendarOff className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No leave applications yet</p>
                    <p className="text-xs mt-1">Click "Apply for Leave" to submit your first request</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Apply Leave Dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Apply for Leave</DialogTitle>
            <DialogDescription>Fill in the details for your leave request</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Leave Type</Label>
              <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="portal-leave-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EL">
                    Earned Leave (EL) {myBalance ? `- ${myBalance.EL || 0} days left` : ''}
                  </SelectItem>
                  <SelectItem value="CL">
                    Casual Leave (CL) {myBalance ? `- ${myBalance.CL || 0} days left` : ''}
                  </SelectItem>
                  <SelectItem value="ML">
                    Medical Leave (ML) {myBalance ? `- ${myBalance.ML || 0} days left` : ''}
                  </SelectItem>
                  <SelectItem value="LOP">Loss of Pay (LOP)</SelectItem>
                  <SelectItem value="ONDUTY">On Duty (ONDUTY)</SelectItem>
                  <SelectItem value="CompOff">
                    Compensatory Off (CompOff) {`- ${compOffBalance || 0} days left`}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Start Date</Label>
                <Input
                  type="date"
                  value={form.startDate}
                  onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))}
                  data-testid="portal-leave-start"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">End Date</Label>
                <Input
                  type="date"
                  value={form.endDate}
                  onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))}
                  data-testid="portal-leave-end"
                />
              </div>
            </div>
            {form.startDate && form.endDate && new Date(form.endDate) >= new Date(form.startDate) && (
              <div className="p-2 rounded-md bg-muted text-center">
                <span className="text-xs text-muted-foreground">Duration: </span>
                <span className="text-sm font-bold font-mono">
                  {Math.ceil((new Date(form.endDate) - new Date(form.startDate)) / 86400000) + 1} day(s)
                </span>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reason</Label>
              <Textarea
                value={form.reason}
                onChange={e => setForm(p => ({ ...p, reason: e.target.value }))}
                placeholder="Describe your reason for leave..."
                rows={3}
                data-testid="portal-leave-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOpen(false)}>Cancel</Button>
            <Button onClick={handleApply} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="portal-submit-leave-btn">
              <Send className="w-4 h-4 mr-2" /> Submit Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
